import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { CircleCheck, CircleEmpty, CirclePlus } from '../../svg';
import { Tooltip } from '../Tooltip';
import { IAddOn } from '../../types';
import { breakpoints, colors } from '../../constants';
import { displayPrice } from '../../utils';

interface IProps extends React.ComponentProps<'li'> {
    addOn: IAddOn;
    className?: string;
    disabled: boolean;
    name: string;
    selected: boolean;
}

const BaseAddOnButton = ({
    addOn,
    className,
    name,
    selected,
    ...props
}: IProps) => {
    const { t } = useTranslation();

    return (
        <Tooltip
            className={className}
            delay={300}
            description={addOn.description}
            price={
                addOn.priceInt > 0
                    ? ` (+ ${displayPrice(addOn.priceInt)})`
                    : `${t('included')}`
            }
            title={addOn.name}
        >
            <li role="button" {...props}>
                {selected ? (
                    <CircleCheck />
                ) : (
                    <>
                        <CirclePlus />
                        <CircleEmpty />
                    </>
                )}
                {name}
            </li>
        </Tooltip>
    );
};

export const AddOnButton = styled(BaseAddOnButton)`
    ${CircleCheck}, ${CircleEmpty}, ${CirclePlus} {
        height: 18px;
        margin-right: 10px;
    }

    li {
        display: flex;
        align-items: center;
        list-style-type: none;
        cursor: ${({ disabled }) => !disabled && 'pointer'};
        pointer-events: ${({ disabled }) => disabled && 'none'};
        border-radius: 4px;
        font-size: 14px;
        font-weight: 700;
        line-height: 1.2;
        background-color: ${({ selected }) =>
            selected ? colors.yellow : 'rgba(255, 255, 255, 0.4)'};
        width: 100%;
        height: 42px;
        padding: 0 20px 0 12px;
        opacity: ${({ disabled }) => (disabled ? 0.2 : 1)};
        transition:
            background-color 0.15s linear,
            opacity 0.1s linear;

        ${CirclePlus} {
            display: none;
        }

        @media (${breakpoints.mediumMin}) {
            font-weight: ${({ selected }) => (selected ? '700' : '400')};
            background-color: ${colors.greenLight};
            height: 32px;
            padding: 0 20px 0 0;

            :hover {
                ${CircleEmpty} {
                    display: none;
                }

                ${CirclePlus} {
                    display: block;
                }
            }
        }
    }
`;
